import { useCallback, useEffect, useRef, useState } from 'react'
import { cityLabel, loadCities, loadedCities, nearestCityTo, type City } from './places'

/**
 * Where the customer is.
 *
 * The site promised food "near you" in the headline, the section kickers and
 * the how-it-works copy, and never once asked. For a hyperlocal marketplace
 * that is not a missing feature so much as an unanswered question: a visitor in
 * Abuja was shown Lagos kitchens and had no way to say so, and the answer was
 * only collected after signup — long after the page had already failed to be
 * relevant.
 *
 * It captures a stated area and, when the customer picks one, a catalogue city
 * (lib/places). With a city the discovery feed ranks kitchens by city-to-city
 * distance; with only a typed area the backend resolves the text to a city
 * itself. Neither is a street address, and the UI must not imply one.
 */

const STORAGE_KEY = 'chow_delivery_area'
const CITY_STORAGE_KEY = 'chow_city_ref'

/**
 * Where a visitor is assumed to be until they say otherwise.
 *
 * Abuja is the launch market, so it is the honest starting assumption rather
 * than showing nothing: a first-time visitor sees a real, orderable catalogue
 * on the first paint instead of an empty page and a question. It is a default,
 * not a lock — the field, the chips and the browser-location button all
 * override it, and the choice persists once made.
 */
export const DEFAULT_AREA = 'Abuja'

/**
 * Rough centroids for the launch cities: the offline fallback for turning a
 * browser fix into a city name when the catalogue has not loaded. With the
 * catalogue, detect() names the nearest of ~800 cities instead.
 *
 * A city-level guess is the honest ceiling for this: reverse geocoding to a
 * street needs a provider and a consent conversation, and the filter downstream
 * is a text match on a neighbourhood label anyway. Naming the city and letting
 * the customer refine it beats naming a street we cannot verify.
 */
const CITY_CENTROIDS: ReadonlyArray<{ city: string; lat: number; lng: number }> = [
  { city: 'Abuja', lat: 9.0765, lng: 7.3986 },
  { city: 'Lagos', lat: 6.5244, lng: 3.3792 },
  { city: 'Port Harcourt', lat: 4.8156, lng: 7.0498 },
  { city: 'Ibadan', lat: 7.3775, lng: 3.947 },
  { city: 'Kano', lat: 12.0022, lng: 8.592 },
]

/** Straight-line distance in kilometres, good enough to pick the nearest city. */
function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

/** The nearest served city to a fix, or "" when the fix is nowhere near one. */
export function nearestCity(lat: number, lng: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return ''
  let best = ''
  let bestKm = Infinity
  for (const c of CITY_CENTROIDS) {
    const km = distanceKm(lat, lng, c.lat, c.lng)
    if (km < bestKm) {
      bestKm = km
      best = c.city
    }
  }
  // Beyond 150km the nearest centroid says nothing useful, and naming a city
  // the customer is not in is worse than admitting we do not know.
  return bestKm <= 150 ? best : ''
}

/**
 * The stored area, or the launch-market default.
 *
 * A visitor who has never chosen starts in Abuja rather than nowhere. The
 * distinction that matters downstream is not "chosen vs default" but "is the
 * catalogue narrowed", and both answers narrow it — so the default is returned
 * as an ordinary area rather than as a separate state the whole UI has to
 * branch on.
 */
function read(): string {
  try {
    return localStorage.getItem(STORAGE_KEY)?.trim() || DEFAULT_AREA
  } catch {
    // Private browsing and locked-down storage both throw here. An area we
    // cannot remember is a smaller problem than a landing page that will not
    // render, so this degrades to the default rather than to nothing.
    return DEFAULT_AREA
  }
}

export type LocateState = 'idle' | 'locating' | 'denied' | 'unavailable'

export interface DeliveryLocation {
  /** The customer's area — their own choice, or DEFAULT_AREA until they make one. */
  area: string
  /**
   * The catalogue city ("gn:<id>") when the customer picked one or GPS found
   * one; "" for a typed area, which the backend resolves itself.
   */
  cityRef: string
  setArea: (next: string) => void
  /** Choose a catalogue city, optionally narrowed by a typed neighbourhood. */
  setCity: (city: City, neighbourhood?: string) => void
  /** Reset to the launch-market default. */
  clear: () => void
  /** Ask the browser where they are. Requires a user gesture on most browsers. */
  detect: () => void
  locating: LocateState
  /**
   * True while `area` is the assumed default rather than the customer's own
   * choice, so the UI can phrase it as an assumption worth correcting instead
   * of stating it back as fact.
   */
  isDefault: boolean
}

function readCityRef(): string {
  try {
    return localStorage.getItem(CITY_STORAGE_KEY)?.trim() || ''
  } catch {
    return ''
  }
}

export function useDeliveryLocation(): DeliveryLocation {
  const [area, setAreaState] = useState<string>(read)
  const [cityRef, setCityRef] = useState<string>(readCityRef)
  const [chosen, setChosen] = useState(() => {
    try { return Boolean(localStorage.getItem(STORAGE_KEY)?.trim()) } catch { return false }
  })
  const [locating, setLocating] = useState<LocateState>('idle')
  const generation = useRef(0)

  useEffect(() => {
    const sync = () => {
      generation.current += 1
      setAreaState(read())
      setCityRef(readCityRef())
      try { setChosen(Boolean(localStorage.getItem(STORAGE_KEY)?.trim())) } catch { /* Storage is optional. */ }
      setLocating('idle')
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY || event.key === CITY_STORAGE_KEY || event.key === null) sync()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('chow-area-change', sync)
    return () => {
      generation.current += 1
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('chow-area-change', sync)
    }
  }, [])

  const save = useCallback((next: string, explicit: boolean, ref = '') => {
    generation.current += 1
    try {
      if (explicit) localStorage.setItem(STORAGE_KEY, next)
      else localStorage.removeItem(STORAGE_KEY)
      if (ref) localStorage.setItem(CITY_STORAGE_KEY, ref)
      else localStorage.removeItem(CITY_STORAGE_KEY)
      window.dispatchEvent(new Event('chow-area-change'))
    } catch { /* Keep the choice in memory when storage is unavailable. */ }
    setAreaState(next)
    setCityRef(ref)
    setChosen(explicit)
    setLocating('idle')
  }, [])
  const setArea = useCallback((next: string) => {
    const normalized = next.trim().slice(0, 120)
    save(normalized || DEFAULT_AREA, Boolean(normalized))
  }, [save])
  const setCity = useCallback((city: City, neighbourhood = '') => {
    const place = neighbourhood.trim().slice(0, 60)
    save(place ? `${place}, ${city.name}` : cityLabel(city), true, city.ref)
  }, [save])
  const clear = useCallback(() => save(DEFAULT_AREA, false), [save])

  const detect = useCallback(() => {
    const request = ++generation.current
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocating('unavailable')
      return
    }
    setLocating('locating')
    // Warm the catalogue while the browser asks for permission.
    void loadCities().catch(() => undefined)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (generation.current !== request) return
        const cities = loadedCities()
        if (cities.length > 0) {
          const city = nearestCityTo(cities, pos.coords.latitude, pos.coords.longitude)
          if (city) save(cityLabel(city), true, city.ref)
          else setLocating('unavailable')
          return
        }
        const city = nearestCity(pos.coords.latitude, pos.coords.longitude)
        if (city) save(city, true)
        else setLocating('unavailable')
      },
      (error) => {
        if (generation.current === request) setLocating(error.code === 1 ? 'denied' : 'unavailable')
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 },
    )
  }, [save])

  return { area, cityRef, setArea, setCity, clear, detect, locating, isDefault: !chosen }
}
